package fyi.moko.hotpl.ui.placeform

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilterChip
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.produceState
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.input.nestedscroll.NestedScrollConnection
import androidx.compose.ui.input.nestedscroll.NestedScrollSource
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.unit.dp
import fyi.moko.hotpl.ui.components.StoredPhotoTile
import fyi.moko.hotpl.ui.components.rememberStoredPhoto
import fyi.moko.hotpl.domain.model.GeoPoint
import fyi.moko.hotpl.domain.model.EvidenceReason
import fyi.moko.hotpl.domain.model.PhotoRef
import fyi.moko.hotpl.domain.model.PlaceStatus
import fyi.moko.hotpl.ui.map.LocationPickerMap
import fyi.moko.hotpl.ui.map.rememberUserLocation
import fyi.moko.hotpl.ui.photo.PhotoAction
import fyi.moko.hotpl.ui.photo.PhotoActionSheet
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PlaceFormScreen(
    placeId: String?,
    onClose: () -> Unit,
) {
    val viewModel: PlaceFormViewModel = koinViewModel()
    LaunchedEffect(placeId) { viewModel.start(placeId) }

    val imagePicker = rememberMultiImagePicker()
    val imageCapturer = rememberImageCapturer()
    val userLocation = rememberUserLocation()
    // For a brand-new place, suggest the current location as the pin (you're usually there).
    // Only a suggestion: picking Want to go lets it go again (see onStatusChange).
    LaunchedEffect(userLocation.target) {
        val target = userLocation.target
        if (placeId == null && viewModel.latitude == null && !viewModel.pinDroppedForWishlist && target != null) {
            viewModel.suggestLocationFromDevice(target.latitude, target.longitude)
        }
    }
    // 방문 추론의 재료. 사진 EXIF가 없을 때 "지금 여기 있나"를 답하는 쪽이다.
    LaunchedEffect(userLocation.target, userLocation.fixAgeMillis) {
        viewModel.onDeviceLocation(userLocation.target, userLocation.fixAgeMillis)
    }

    PlaceFormContent(
        state = PlaceFormContentState(
            name = viewModel.name,
            tagsText = viewModel.tagsText,
            allTags = viewModel.allTags,
            selectedTags = viewModel.selectedTags,
            note = viewModel.note,
            isEditing = viewModel.isEditing,
            locationContext = viewModel.locationContext,
            latitude = viewModel.latitude,
            longitude = viewModel.longitude,
            nameError = viewModel.nameError,
            showDuplicateWarning = viewModel.showDuplicateWarning,
            saveError = viewModel.saveError,
            resolvingLocationContext = viewModel.resolvingLocationContext,
            loading = viewModel.loading,
            saving = viewModel.saving,
            isBusy = viewModel.isBusy,
            canSave = viewModel.canSave,
            isDirty = viewModel.isDirty,
            status = viewModel.status,
            statusReason = viewModel.statusReason,
            pinDroppedForWishlist = viewModel.pinDroppedForWishlist,
        ),
        onClose = onClose,
        onSave = { viewModel.save(onSaved = onClose) },
        onSaveAnyway = { viewModel.saveAnyway(onSaved = onClose) },
        onDismissDuplicateWarning = viewModel::dismissDuplicateWarning,
        onDismissSaveError = viewModel::dismissSaveError,
        onNameChange = viewModel::onNameChange,
        onToggleTag = viewModel::toggleTag,
        onNoteChange = viewModel::onNoteChange,
        onStatusChange = viewModel::onStatusChange,
        onLocationChange = viewModel::onLocationChange,
        onClearLocation = viewModel::clearLocation,
        userTarget = userLocation.target,
        myLocationEnabled = userLocation.granted,
        existingPhotos = viewModel.existingPhotos,
        pendingPhotos = viewModel.pendingPhotos,
        classifyingPhotos = viewModel.classifyingPhotos,
        onPickPhotos = { imagePicker.pick(viewModel::onPhotosPicked) },
        // A capture is a one-photo pick: same intake, so it is downscaled, classified
        // and counted against MAX_PLACE_PHOTOS exactly like one chosen from the library.
        onCapturePhoto = { imageCapturer.capture { bytes -> bytes?.let { viewModel.onPhotosPicked(listOf(PickedImage(it))) } } },
        cameraAvailable = imageCapturer.isAvailable,
        onExistingPhotoClick = { fileName ->
            viewModel.openPhotoActions(PlaceFormPhotoTarget.Existing(fileName))
        },
        onPendingPhotoClick = { index -> viewModel.openPhotoActions(PlaceFormPhotoTarget.Pending(index)) },
    )

    if (viewModel.photoActionTarget != null) {
        // 상세·방문 시트와 같은 본문을 쓴다 — 같은 모양 타일은 같은 뜻이어야 한다(R-6).
        PhotoActionSheet(
            actions = listOf(PhotoAction("Delete this photo", viewModel::removeTargetedPhoto)),
            onDismiss = viewModel::dismissPhotoActions,
        )
    }
}

data class PlaceFormContentState(
    val name: String,
    val tagsText: String,
    val allTags: List<String> = emptyList(),
    val selectedTags: List<String> = emptyList(),
    val note: String,
    val isEditing: Boolean,
    val locationContext: String?,
    val latitude: Double?,
    val longitude: Double?,
    val nameError: String?,
    val showDuplicateWarning: Boolean,
    val saveError: String? = null,
    val resolvingLocationContext: Boolean,
    val loading: Boolean,
    val saving: Boolean,
    val isBusy: Boolean,
    val canSave: Boolean,
    val isDirty: Boolean = false,
    val status: PlaceStatus = PlaceStatus.VISITED,
    val statusReason: EvidenceReason = EvidenceReason.UNKNOWN,
    val pinDroppedForWishlist: Boolean = false,
)

@Composable
fun PlaceFormContent(
    state: PlaceFormContentState,
    onClose: () -> Unit,
    onSave: () -> Unit,
    onSaveAnyway: () -> Unit,
    onDismissDuplicateWarning: () -> Unit,
    onDismissSaveError: () -> Unit = {},
    onNameChange: (String) -> Unit,
    onToggleTag: (String) -> Unit = {},
    onNoteChange: (String) -> Unit,
    onStatusChange: (PlaceStatus) -> Unit = {},
    onLocationChange: (Double, Double) -> Unit,
    onClearLocation: () -> Unit,
    userTarget: GeoPoint?,
    myLocationEnabled: Boolean,
    existingPhotos: List<PhotoRef> = emptyList(),
    pendingPhotos: List<PendingPlacePhoto> = emptyList(),
    classifyingPhotos: Boolean = false,
    onPickPhotos: () -> Unit = {},
    onCapturePhoto: () -> Unit = {},
    cameraAvailable: Boolean = false,
    onExistingPhotoClick: (fileName: String) -> Unit = {},
    onPendingPhotoClick: (index: Int) -> Unit = {},
    locationSection: @Composable (
        latitude: Double?,
        longitude: Double?,
        onPick: (Double, Double) -> Unit,
        onClear: () -> Unit,
        locationContext: String?,
        resolvingLocationContext: Boolean,
        userTarget: GeoPoint?,
        myLocationEnabled: Boolean,
        enabled: Boolean,
        ) -> Unit = { latitude, longitude, onPick, onClear, locationContext, resolvingLocationContext, target, locationEnabled, enabled ->
        LocationSection(
            latitude = latitude,
            longitude = longitude,
            onPick = onPick,
            onClear = onClear,
                locationContext = locationContext,
                resolvingLocationContext = resolvingLocationContext,
            userTarget = target,
            myLocationEnabled = locationEnabled,
            enabled = enabled,
        )
    },
) {
    // Dismiss the soft keyboard when the user drags the form, so it never sits on top
    // of the location map below the text fields (where you pick the pin / see geocoding).
    // We can't rely on NestedScrollSource here: when a lower field like "Notes" gains
    // focus, its programmatic bring-into-view scroll is reported as UserInput on some
    // devices, so filtering by source would still bounce the keyboard back out on the
    // very tap that opened it. Instead we require a finger to actually be on screen —
    // a bring-into-view scroll happens with no pointer down, a real drag does not.
    val focusManager = LocalFocusManager.current
    val pointerDown = remember { mutableStateOf(false) }
    val dismissKeyboardOnScroll = remember(focusManager) {
        object : NestedScrollConnection {
            override fun onPreScroll(available: Offset, source: NestedScrollSource): Offset {
                if (available.y != 0f && pointerDown.value) {
                    focusManager.clearFocus()
                }
                return Offset.Zero
            }
        }
    }

    // Guard against losing typed input: Cancel confirms only when there are edits.
    var showDiscardConfirm by remember { mutableStateOf(false) }
    val requestClose = { if (state.isDirty) showDiscardConfirm = true else onClose() }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(if (state.isEditing) "Edit place" else "Add place") },
                navigationIcon = {
                    TextButton(onClick = requestClose) { Text("Cancel") }
                },
                actions = {
                    TextButton(
                        onClick = onSave,
                        enabled = state.canSave,
                    ) {
                        Text(if (state.saving) "Saving..." else "Save")
                    }
                }
            )
        },
    ) { innerPadding ->
        if (state.loading && state.isEditing) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(innerPadding),
                contentAlignment = Alignment.Center,
            ) {
                CircularProgressIndicator()
            }
        } else {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(innerPadding)
                    .imePadding()
                    // Track whether a finger is physically on the form (Initial pass, no
                    // consumption) so the nested-scroll handler above can tell a real drag
                    // from a programmatic bring-into-view scroll.
                    .pointerInput(Unit) {
                        awaitPointerEventScope {
                            while (true) {
                                val event = awaitPointerEvent(PointerEventPass.Initial)
                                pointerDown.value = event.changes.any { it.pressed }
                            }
                        }
                    }
                    .nestedScroll(dismissKeyboardOnScroll)
                    .verticalScroll(rememberScrollState())
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp),
            ) {
                PhotoSection(
                    existingPhotos = existingPhotos,
                    pendingPhotos = pendingPhotos,
                    classifying = classifyingPhotos,
                    onPickPhotos = onPickPhotos,
                    onCapturePhoto = onCapturePhoto,
                    cameraAvailable = cameraAvailable,
                    onExistingPhotoClick = onExistingPhotoClick,
                    onPendingPhotoClick = onPendingPhotoClick,
                    enabled = !state.isBusy,
                )
                OutlinedTextField(
                    value = state.name,
                    onValueChange = onNameChange,
                    label = { Text("Place name") },
                    singleLine = true,
                    enabled = !state.isBusy,
                    isError = state.nameError != null,
                    supportingText = {
                        state.nameError?.let { Text(it) }
                    },
                    modifier = Modifier.fillMaxWidth(),
                )
                TagsSection(
                    allTags = state.allTags,
                    selectedTags = state.selectedTags,
                    onToggleTag = onToggleTag,
                    enabled = !state.isBusy,
                )
                OutlinedTextField(
                    value = state.note,
                    onValueChange = onNoteChange,
                    label = { Text("Notes") },
                    enabled = !state.isBusy,
                    modifier = Modifier
                        .fillMaxWidth()
                        .heightIn(min = 120.dp),
                )
                if (!state.isEditing) {
                    VisitStatusSection(
                        status = state.status,
                        reason = state.statusReason,
                        pinDropped = state.pinDroppedForWishlist,
                        enabled = !state.isBusy,
                        onStatusChange = onStatusChange,
                    )
                }
                // "Revisit soon"(liked) 스위치는 여기서 뺐다(2026-09-17). 다시 가고 싶다는 뜻은 가 본
                // 뒤에야 생기는데, 이 폼은 새 장소·안 가본 곳에도 뜨고 켜도 배지는 방문 전까지 안
                // 보였다 — 처음 쓰는 사람에게 맥락이 없었다. 상세의 하트와 Log visit 시트가 그 자리다.

                locationSection(
                    state.latitude,
                    state.longitude,
                    onLocationChange,
                    onClearLocation,
                    state.locationContext,
                    state.resolvingLocationContext,
                    userTarget,
                    myLocationEnabled,
                    !state.isBusy,
                )
            }
        }
    }

    if (state.showDuplicateWarning) {
        AlertDialog(
            onDismissRequest = onDismissDuplicateWarning,
            text = { Text("A place with the same name already exists.") },
            confirmButton = {
                TextButton(onClick = onSaveAnyway) { Text("Save anyway") }
            },
            dismissButton = {
                TextButton(
                    onClick = onDismissDuplicateWarning,
                    modifier = Modifier.testTag("duplicate-warning-cancel"),
                ) { Text("Cancel") }
            },
        )
    }

    if (state.saveError != null) {
        AlertDialog(
            onDismissRequest = onDismissSaveError,
            text = { Text(state.saveError) },
            confirmButton = {
                TextButton(onClick = onDismissSaveError) { Text("OK") }
            },
        )
    }

    if (showDiscardConfirm) {
        AlertDialog(
            onDismissRequest = { showDiscardConfirm = false },
            title = { Text("Discard changes?") },
            text = { Text("You have unsaved changes. Leaving now will discard them.") },
            confirmButton = {
                TextButton(
                    onClick = {
                        showDiscardConfirm = false
                        onClose()
                    },
                ) {
                    Text("Discard")
                }
            },
            dismissButton = {
                TextButton(onClick = { showDiscardConfirm = false }) { Text("Keep editing") }
            },
        )
    }
}

/**
 * The place's photo gallery: existing photos (loaded from disk by file name) plus any
 * newly picked this session, each removable individually, and two ways to add one --
 * the camera, and the photo library.
 * These always land in [fyi.moko.hotpl.domain.model.Place.photos] — there is no
 * place/visit destination choice here, unlike [fyi.moko.hotpl.ui.detail.VisitPhotoSection].
 */
@Composable
private fun PhotoSection(
    existingPhotos: List<PhotoRef>,
    pendingPhotos: List<PendingPlacePhoto>,
    classifying: Boolean,
    onPickPhotos: () -> Unit,
    onCapturePhoto: () -> Unit,
    cameraAvailable: Boolean,
    onExistingPhotoClick: (fileName: String) -> Unit,
    onPendingPhotoClick: (index: Int) -> Unit,
    enabled: Boolean,
) {
    val totalCount = existingPhotos.size + pendingPhotos.size
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Text("Photos", style = MaterialTheme.typography.titleSmall)
            if (classifying) {
                Text(
                    "Sorting…",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
        if (totalCount > 0) {
            Row(
                modifier = Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                existingPhotos.forEach { photo ->
                    ExistingPhotoTile(
                        fileName = photo.fileName,
                        enabled = enabled,
                        onClick = { onExistingPhotoClick(photo.fileName) },
                    )
                }
                pendingPhotos.forEachIndexed { index, photo ->
                    PendingPhotoTile(
                        bytes = photo.bytes,
                        enabled = enabled,
                        onClick = { onPendingPhotoClick(index) },
                    )
                }
            }
        }
        val canAdd = enabled && !classifying && totalCount < PlaceFormViewModel.MAX_PLACE_PHOTOS
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            // Camera first and filled: photographing the place you are standing in is what
            // users reach for, and picking from the library is the fallback for a place
            // already visited. A device without a camera gets the picker at full width
            // rather than a half that fails on tap.
            if (cameraAvailable) {
                Button(
                    onClick = onCapturePhoto,
                    enabled = canAdd,
                    modifier = Modifier.weight(1f),
                ) {
                    Text("Take photo")
                }
            }
            OutlinedButton(
                onClick = onPickPhotos,
                enabled = canAdd,
                modifier = Modifier.weight(1f),
            ) {
                Text(if (totalCount == 0) "Add photo" else "Add more")
            }
        }
    }
}

/**
 * A photo already on the place. A tap opens the photo's action sheet, so an unavailable
 * photo does not retry on tap — the sheet is what the user wants at that point anyway.
 */
@Composable
private fun ExistingPhotoTile(fileName: String, enabled: Boolean, onClick: () -> Unit) {
    StoredPhotoTile(
        handle = rememberStoredPhoto(fileName),
        size = 84.dp,
        onClick = onClick,
        enabled = enabled,
        retryOnTap = false,
        contentDescription = "Photo options",
    )
}

@Composable
private fun PendingPhotoTile(bytes: ByteArray, enabled: Boolean, onClick: () -> Unit) {
    val bitmap = remember(bytes) { decodeImageBitmap(bytes) }
    PhotoTile(bitmap = bitmap, enabled = enabled, onClick = onClick)
}

@Composable
private fun PhotoTile(bitmap: ImageBitmap?, enabled: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .size(84.dp)
            .clip(RoundedCornerShape(10.dp))
            .background(MaterialTheme.colorScheme.surfaceVariant)
            .clickable(enabled = enabled, onClick = onClick),
    ) {
        bitmap?.let {
            Image(
                bitmap = it,
                contentDescription = "Photo options",
                contentScale = ContentScale.Crop,
                modifier = Modifier.fillMaxSize(),
            )
        }
    }
}

/**
 * 가본 곳인지 아닌지. 값은 [fyi.moko.hotpl.domain.model.inferVisit]이 먼저 골라 두고, 여기서는
 * 그것을 **보여 주고 바꾸게** 한다.
 *
 * 왜 굳이 보이게 두는가: 추론은 조용히 정하지만 결과까지 숨기면 틀렸을 때 알아챌 길이 없다.
 * 조용한 데다 되돌릴 수 없는 동작이 리뷰어가 사진을 잃은 이유였다(2026-09-08 R-6).
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun VisitStatusSection(
    status: PlaceStatus,
    reason: EvidenceReason,
    pinDropped: Boolean,
    enabled: Boolean,
    onStatusChange: (PlaceStatus) -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text("Have you been?", style = MaterialTheme.typography.titleSmall)
        FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            FilterChip(
                selected = status == PlaceStatus.VISITED,
                onClick = { onStatusChange(PlaceStatus.VISITED) },
                enabled = enabled,
                label = { Text("Been here") },
            )
            FilterChip(
                selected = status == PlaceStatus.WISHLIST,
                onClick = { onStatusChange(PlaceStatus.WISHLIST) },
                enabled = enabled,
                label = { Text("Want to go") },
            )
        }
        val hint = if (pinDropped) {
            // The device-suggested pin was let go: a place you haven't been to isn't where you stand.
            "Dropped the pin at your current spot — tap the map to set where it is."
        } else {
            visitStatusHint(reason)
        }
        hint?.let {
            Text(
                text = it,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

/** 왜 그렇게 골랐는지 한 줄. 근거가 없을 때는 아무 말도 하지 않는다. */
private fun visitStatusHint(reason: EvidenceReason): String? = when (reason) {
    EvidenceReason.PHOTO_LOCATION -> "From where your photo was taken."
    EvidenceReason.HERE_NOW -> "You're here right now."
    EvidenceReason.FAR_AWAY -> "You're somewhere else, so this starts as a place to go."
    EvidenceReason.UNKNOWN -> null
}

/** Existing tags are shown as toggleable chips; new ones can be typed and added below. */
@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun TagsSection(
    allTags: List<String>,
    selectedTags: List<String>,
    onToggleTag: (String) -> Unit,
    enabled: Boolean,
) {
    var newTagInput by rememberSaveable { mutableStateOf("") }
    val chipTags = (allTags + selectedTags).distinct().sorted()

    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text("Tags", style = MaterialTheme.typography.titleSmall)
        if (chipTags.isNotEmpty()) {
            FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                chipTags.forEach { tag ->
                    FilterChip(
                        selected = tag in selectedTags,
                        onClick = { onToggleTag(tag) },
                        enabled = enabled,
                        label = { Text(tag) },
                    )
                }
            }
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            OutlinedTextField(
                value = newTagInput,
                onValueChange = { newTagInput = it },
                placeholder = { Text("New tag") },
                singleLine = true,
                enabled = enabled,
                modifier = Modifier.weight(1f),
            )
            TextButton(
                onClick = {
                    onToggleTag(newTagInput)
                    newTagInput = ""
                },
                enabled = enabled && newTagInput.isNotBlank(),
            ) {
                Text("Add")
            }
        }
    }
}

@Composable
private fun LocationSection(
    latitude: Double?,
    longitude: Double?,
    onPick: (Double, Double) -> Unit,
    onClear: () -> Unit,
    locationContext: String?,
    resolvingLocationContext: Boolean,
    userTarget: GeoPoint?,
    myLocationEnabled: Boolean,
    enabled: Boolean,
) {
    val hasLocation = latitude != null && longitude != null
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text("Location", style = MaterialTheme.typography.titleSmall)
            if (hasLocation) {
                TextButton(onClick = onClear, enabled = enabled) { Text("Clear location") }
            }
        }
        Text(
            text = if (hasLocation) {
                "Tap the map to move the pin (${formatCoord(latitude)}, ${formatCoord(longitude)})"
            } else {
                "Tap the map to set a location (optional)"
            },
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        when {
            resolvingLocationContext -> {
                Text(
                    text = "Resolving nearby area...",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }

            !locationContext.isNullOrBlank() -> {
                Text(
                    text = locationContext,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
        LocationPickerMap(
            latitude = latitude,
            longitude = longitude,
            onPick = { lat, lng -> if (enabled) onPick(lat, lng) },
            userTarget = userTarget,
            myLocationEnabled = myLocationEnabled,
            modifier = Modifier
                .fillMaxWidth()
                .height(240.dp)
                .clip(MaterialTheme.shapes.medium),
        )
    }
}

// "%.5f"-equivalent for a coordinate display. `String.format` isn't available in
// common Kotlin, so round to 5 decimals and pad the fractional part.
private fun formatCoord(value: Double): String {
    val rounded = kotlin.math.round(value * 100_000.0) / 100_000.0
    val s = rounded.toString()
    val dot = s.indexOf('.')
    return if (dot < 0) {
        "$s.00000"
    } else {
        val frac = s.substring(dot + 1)
        s + "0".repeat((5 - frac.length).coerceAtLeast(0))
    }
}